import { ApiActions } from './apiActions';
import apiCall from './apiCall';
import { canUserDo, getTierLimits, TIER_PERMISSIONS } from './tierPermissions';
import { clog, cinfo, cerror } from './consoleLogger';

const ACCOUNT_CACHE_KEY = 'trip147_account_data';
const ACCOUNT_CACHE_TIMESTAMP_KEY = 'trip147_account_timestamp';
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Get account data with caching
 * @returns {Promise<Object|null>} Account data or null if not available
 */
export async function getCachedAccountData() {
  try {
    // Check if we have cached data
    const cachedData = localStorage.getItem(ACCOUNT_CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(ACCOUNT_CACHE_TIMESTAMP_KEY);
    
    if (cachedData && cachedTimestamp) {
      const now = Date.now();
      const cacheAge = now - parseInt(cachedTimestamp, 10);
      
      // If cache is still valid (less than 5 minutes old), return cached data
      if (cacheAge < CACHE_DURATION) {
        return JSON.parse(cachedData);
      }
    }
    
    // Cache expired or doesn't exist, fetch fresh data
    const response = await apiCall('/api/get?action=' + ApiActions.GET_ACCOUNT_DATA, {}, 'GET');
    
    if (response.success && response.account) {
      // Cache the data
      localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(response.account));
      localStorage.setItem(ACCOUNT_CACHE_TIMESTAMP_KEY, Date.now().toString());
      return response.account;
    }
    
    return null;
  } catch (error) {
    cerror('Error fetching account data:', error);
    // Return cached data if available, even if expired
    const cachedData = localStorage.getItem(ACCOUNT_CACHE_KEY);
    if (cachedData) {
      return JSON.parse(cachedData);
    }
    return null;
  }
}

/**
 * Clear cached account data (call on logout)
 */
export function clearAccountCache() {
  localStorage.removeItem(ACCOUNT_CACHE_KEY);
  localStorage.removeItem(ACCOUNT_CACHE_TIMESTAMP_KEY);
}

/**
 * Check if user is admin (using cached data)
 * @returns {Promise<boolean>} True if user is admin
 */
export async function isAdmin() {
  const account = await getCachedAccountData();
  return account?.subscription?.tier === 'admin';
}

/**
 * Force refresh account data (bypass cache)
 * @returns {Promise<Object|null>} Fresh account data
 */
export async function refreshAccountData() {
  try {
    // Clear cache first
    clearAccountCache();
    
    // Force fresh API call (bypass all cache logic)
    const response = await apiCall('/api/get?action=' + ApiActions.GET_ACCOUNT_DATA, {}, 'GET');
    
    if (response.success && response.account) {
      // Cache the fresh data
      localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(response.account));
      localStorage.setItem(ACCOUNT_CACHE_TIMESTAMP_KEY, Date.now().toString());
      return response.account;
    }
    
    return null;
  } catch (error) {
    cerror('Error refreshing account data:', error);
    return null;
  }
}

/**
 * Get user's subscription tier (using cached data)
 * @returns {Promise<string>} Tier (free, pro, admin)
 */
export async function getUserTier() {
  const account = await getCachedAccountData();
  return account?.subscription?.tier || 'free';
}

/**
 * Check if user can perform an action (using cached data)
 * @param {string} permission - The permission to check (e.g., 'canShare')
 * @returns {Promise<boolean>} True if user has permission
 */
export async function canUser(permission) {
  const tier = await getUserTier();
  return canUserDo(tier, permission);
}

/**
 * Get user's limits (using cached data)
 * @returns {Promise<Object>} Limits object
 */
export async function getUserLimits() {
  const tier = await getUserTier();
  return getTierLimits(tier);
}

/**
 * Get all tier permissions (for display purposes)
 * @returns {Object} TIER_PERMISSIONS object
 */
export function getAllTierPermissions() {
  return TIER_PERMISSIONS;
}

/**
 * Get smart redirect path based on user's email verification and plan count
 * This is the SINGLE SOURCE OF TRUTH for redirect logic after login/registration
 * 
 * @param {Object} userData - User data from localStorage (must have emailVerified field)
 * @param {boolean} forceRefresh - If true, force refresh from server. If false, use cache if available. Default: true
 * @returns {Promise<string>} - Path to redirect to
 */
export async function getSmartRedirectPath(userData, forceRefresh = true) {
  cinfo('[getSmartRedirectPath] START - userData:', userData, 'forceRefresh:', forceRefresh);
  
  // No user data - redirect to home
  if (!userData) {
    cinfo('[getSmartRedirectPath] No userData → redirect to /');
    return '/';
  }
  
  // Check if email is verified first
  if (!userData.emailVerified) {
    cinfo('[getSmartRedirectPath] Email NOT verified → redirect to /email-verification-required');
    return '/email-verification-required';
  }
  
  cinfo('[getSmartRedirectPath] Email verified ✅ - fetching account data...');
  
  // Email is verified - check if new user (no trips created yet)
  try {
    // Get account data (force refresh or use cache)
    const accountData = forceRefresh 
      ? await refreshAccountData() 
      : await getCachedAccountData() || await refreshAccountData();
    
    cinfo('[getSmartRedirectPath] Account data:', accountData);
    cinfo('[getSmartRedirectPath] Usage:', accountData?.usage);
    cinfo('[getSmartRedirectPath] Plans created:', accountData?.usage?.plansCreated);
    
    // Get plans count - handle all falsy values (undefined, null, 0, false, '')
    const plansCreated = accountData?.usage?.plansCreated ?? 0;
    
    cinfo('[getSmartRedirectPath] Plans created (normalized):', plansCreated);
    
    // If plansCreated is 0 (or was undefined/null and defaulted to 0), redirect to AI Creator
    if (plansCreated === 0) {
      // New user or no trips yet - redirect to AI Plan Creator
      cinfo('[getSmartRedirectPath] ✅ NO TRIPS → redirect to /trip-creator');
      return '/trip-creator';
    } else {
      // Existing user with trips - redirect to Plans page
      cinfo('[getSmartRedirectPath] ✅ HAS', plansCreated, 'TRIPS → redirect to /plans');
      return '/plans';
    }
  } catch (error) {
    cerror('[getSmartRedirectPath] ❌ ERROR:', error);
    // For new users, default to AI Creator on error (safer than empty plans page)
    cinfo('[getSmartRedirectPath] Error fallback → redirect to /trip-creator');
    return '/trip-creator';
  }
}

/**
 * Sync tour status from account.usage to localStorage (called after login)
 * Only syncs if localStorage doesn't already have the value
 */
export async function syncTourStatusFromAccount() {
  try {
    const accountData = await refreshAccountData();
    if (accountData?.usage) {
      if (accountData.usage.guidedTourOverviewTabSeen === true) {
        if (localStorage.getItem('guidedTourOverviewTabSeen') !== 'true') {
          localStorage.setItem('guidedTourOverviewTabSeen', 'true');
          cinfo('[syncTourStatusFromAccount] Synced guidedTourOverviewTabSeen to localStorage');
        }
      }
      if (accountData.usage.guidedTourDaysTabSeen === true) {
        if (localStorage.getItem('guidedTourDaysTabSeen') !== 'true') {
          localStorage.setItem('guidedTourDaysTabSeen', 'true');
          cinfo('[syncTourStatusFromAccount] Synced guidedTourDaysTabSeen to localStorage');
        }
      }
    }
  } catch (error) {
    cerror('Error syncing tour status from account:', error);
  }
}

/**
 * Update account usage (e.g., mark guided tours as seen)
 * @param {Object} usageUpdates - Object with usage fields to update (e.g., { guidedTourOverviewTabSeen: true })
 * @returns {Promise<boolean>} True if update was successful
 */
export async function updateAccountUsage(usageUpdates) {
  try {
    const response = await apiCall('/api/set?action=' + ApiActions.UPDATE_ACCOUNT_SETTINGS, {
      usage: usageUpdates
    }, 'POST');
    
    if (response.success) {
      // Clear cache to force refresh on next getCachedAccountData call
      clearAccountCache();
      return true;
    }
    
    return false;
  } catch (error) {
    cerror('Error updating account usage:', error);
    return false;
  }
}

